import { useEffect, useState } from "react";

import { BillingClaimInvoiceStatus } from "@/Billing/Models/BillingClaimInvoiceStatus";
import { BillingClaimStatusInboxModel } from "@/Billing/Models/UI/BillingClaimStatusInboxModel";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TimeSelector } from "@/Common/Components/Inputs/TimeSelector";
import { SearchSettingsFooter } from "@/Common/Components/Settings/SearchSettingsFooter";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";

import styles from "./BillingClaimStatusSettings.module.scss";

interface BillingClaimStatusSettingsProps {
    model: BillingClaimStatusInboxModel;
    onSaveChanges: (isSaveSettingsChecked: boolean) => void;
    onClose: () => void;
    onColumnOrderingPopupOpenClick: () => void;
}

const overlayKey: string = "BillingFilterSettingPopupOverlay";

function BillingClaimStatusSettings(props: BillingClaimStatusSettingsProps): JSX.Element {
    const { getText } = useTranslation();

    const [isSaveSettingsChecked, setIsSaveSettingsChecked] = useState<boolean>(false);
    const [filteredClinicians, setFilteredClinicians] = useState<EmployeeUIModel[]>([]);
    const [filteredInvoiceStatus] = useState([BillingClaimInvoiceStatus.NoNote, BillingClaimInvoiceStatus.NoPaymentRequest, BillingClaimInvoiceStatus.PaymentRequest]);

    function toggleSelectAllClinicians(isAllSelected: boolean): void {
        sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_SelectedProvidersChanged, { selectedProviderIds: isAllSelected ? filteredClinicians.map(clinician => clinician.Id) : [] });
    }

    function toggleSelectAllStatus(isAllSelected: boolean): void {
        sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_SelectedInvoiceStatusesChanged, { selectedInvoiceStatus: isAllSelected ? filteredInvoiceStatus.map(clinician => clinician) : [] });
    }

    useEffect(() => {
        const selectedSiteIdsDictionary = props.model?.selectedSiteIds.distinct().filter(p => !!p).toDictionary(p => p.toString());
        let filteredClinicianBySiteAndName = [...props.model.allClinicians];

        if (!!selectedSiteIdsDictionary && Object.keys(selectedSiteIdsDictionary).length) {
            filteredClinicianBySiteAndName = filteredClinicianBySiteAndName.filter(p => p.Sites.any(site => !!selectedSiteIdsDictionary[site]));
        }

        if (!IsNullOrWhiteSpace(props.model?.providerSearchText)) {
            filteredClinicianBySiteAndName = filteredClinicianBySiteAndName.filterSearch(props.model?.providerSearchText, p => p.Name);
        }

        setFilteredClinicians(filteredClinicianBySiteAndName);
    }, [props.model?.selectedSiteIds, props.model?.providerSearchText, props.model.allClinicians]);

    useEffect(() => {
        overlayStore.showOverlay(overlayKey, {
            transparent: true,
            onClicked: () => {
                props.onSaveChanges(isSaveSettingsChecked);
                props.onClose();
            }
        });
    }, [isSaveSettingsChecked, props]);

    useEffect(() => () => {
        overlayStore.hideOverlay(overlayKey);
    }, []);

    function getInvoiceStatusText(status: BillingClaimInvoiceStatus): string {
        switch (status) {
            case BillingClaimInvoiceStatus.NoNote:
                return getText("BillingReconciliation_NoNoteLabel");
            case BillingClaimInvoiceStatus.NoPaymentRequest:
                return getText("BillingReconciliation_NoPaymentRequestLabel");
            case BillingClaimInvoiceStatus.PaymentRequest:
                return getText("BillingReconciliation_WithPaymentRequestLabel");
        }
    }

    function onProviderSearchTextChange(newText: string): void {
        sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_ProviderFilterTextChanged, { providerSearchText: newText });
    }

    function onDateFromTodayClick(e: React.MouseEvent<HTMLAnchorElement>): void {
        e.preventDefault();
        sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_FromDateChanged, { value: new Date() });
    }

    function onDateToNowClick(e: React.MouseEvent<HTMLAnchorElement>): void {
        e.preventDefault();
        sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_ToDateChanged, { value: new Date(), followCurrentTime: true });
    }

    return (
        <div className={`${styles.filterSettingsContainer} ${styles.inbox}`} data-cy="settingsContainer">
            <div className={styles.headers}>
                <div>
                    {getText("DateLabel").toUpperCase()}
                </div>
                <div className="checkBoxHeader">
                    <CheckboxControl
                        label={getText("SitesLabel").toUpperCase()}
                        checked={props.model?.selectedSiteIds.length === props.model.orderedSites.length}
                        onChange={isChecked => sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_SelectAllSites, { isChecked })}
                    />
                </div>
                <div className="checkBoxHeader">
                    <CheckboxControl
                        label={getText("ProviderLabel").toUpperCase()}
                        checked={props.model?.selectedProviderIds?.length === filteredClinicians.length}
                        onChange={isChecked => toggleSelectAllClinicians(isChecked)}
                    />
                </div>
                <div className="checkBoxHeader">
                    <CheckboxControl
                        label={getText("BillingReconciliation_InvoiceStatusLabel").toUpperCase()}
                        checked={props.model?.selectedInvoiceStatus?.length === filteredInvoiceStatus.length && filteredInvoiceStatus.length > 0}
                        onChange={isChecked => toggleSelectAllStatus(isChecked)}
                    />
                </div>
                <div className="checkBoxHeader">
                    <CheckboxControl
                        label={getText("SettingsLabel").toUpperCase()}
                        checked={props.model?.isIgnored}
                        onChange={isChecked => sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_IsIgnoredChanged, { isIgnored: isChecked })}
                    />
                </div>
            </div>
            <div className={styles.columns}>
                <div className="date">
                    <label className="bold">{getText("StartDateLabel")}</label>
                    <div style={{ marginBottom: "9px" }}>
                        <DateSelector
                            id="BillingClaimStatusDateFrom"
                            date={props.model?.dateFrom}
                            dateFormat="yyyy/MM/dd"
                            onChangeCallback={value => sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_FromDateChanged, { value })}
                        />
                        <a onClick={e => onDateFromTodayClick(e)}>{getText("TodayLabel")}</a>
                    </div>

                    <label className="bold">{getText("EndDateDLabel")}</label>
                    <div className={styles.dateTimeContainer}>
                        <div>
                            <DateSelector
                                id="BillingClaimStatusDateTo"
                                date={props.model.followCurrentTime ? new Date() : props.model?.dateTo}
                                width={120}
                                height={36}
                                dateFormat="yyyy/MM/dd"
                                onChangeCallback={value => sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_ToDateChanged, { value })}
                            />
                            <a onClick={e => onDateToNowClick(e)}>{getText("NowLabel")}</a>
                        </div>
                        <TimeSelector
                            id="BillingClaimStatusTimeTo"
                            date={props.model.followCurrentTime ? new Date() : props.model?.dateTo}
                            dateFormat="HH:mm"
                            width={80}
                            onChangeCallback={value => sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_ToDateChanged, { value })}
                        />
                    </div>

                    <CheckboxControl
                        label={getText("EndDateFollowsCurrentTimeLabel")}
                        checked={props.model?.followCurrentTime}
                        onChange={isChecked => sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_ToDateChanged, { value: new Date(), followCurrentTime: isChecked })}
                    />
                </div>
                <div className={styles.elements}>
                    {props.model.orderedSites.map(site => (
                        <CheckboxControl
                            id={site.Model.Id}
                            label={site.Model.ClinicalName}
                            checked={props.model?.selectedSiteIds.contains(site.Model.Id)}
                            onChange={isChecked => sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_SelectSite, {
                                isChecked,
                                siteId: site.Model.Id
                            })}
                            key={`billingclaimstatus_${site.Model.Id}`}
                        />
                    ))}
                </div>
                <div className={styles.elements}>
                    <TextboxControl
                        className="form-control"
                        value={props.model?.providerSearchText ?? ""}
                        onChange={(newText: string) => onProviderSearchTextChange(newText)}
                        placeholder={getText("SearchLabel").toUpperCase()}
                    />
                    <CheckedListControl
                        dataCy="billing-claim-status-settings-clinician"
                        DataSource={filteredClinicians.map(p => new EmployeeUIModel({ ...p, Checked: props.model.selectedProviderIds.any(id => p.Id === id) })).toImmutable()}
                        onChange={(isChecked, checkboxId) => sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_SelectProvider, {
                            isChecked,
                            clinicianId: GuidHelper.AsGuid(checkboxId)
                        })}
                        sort
                        limitTextWidth
                        idModifier="clinicians"
                    />
                </div>
                <div className={styles.elements}>
                    {filteredInvoiceStatus.map((status, index) => (
                        <CheckboxControl
                            id={`status_${index}`}
                            label={getInvoiceStatusText(status)}
                            checked={props.model?.selectedInvoiceStatus.contains(status)}
                            onChange={isChecked => sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_SelectInvoiceStatus, {
                                isChecked,
                                status
                            })}
                            key={`status_${index}`}
                        />
                    ))}
                </div>
                <div className={styles.elements}>
                    <CheckboxControl
                        id="includeIgnored"
                        label={getText("BillingReconciliation_IncludeIgnoredLabel")}
                        checked={props.model?.isIgnored}
                        onChange={isChecked => sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_IsIgnoredChanged, { isIgnored: isChecked })}
                        key="includeIgnored"
                    />
                </div>

            </div>
            <div className={styles.footer}>
                <SearchSettingsFooter
                    onClose={() => props.onClose()}
                    onOk={() => props.onSaveChanges(isSaveSettingsChecked)}
                    onColumnOrderingButtonClick={() => props.onColumnOrderingPopupOpenClick()}
                    onResultsPerPageChange={pageSize => sessionStore.dispatch(LocalOperations.Billing.ClaimStatus_SearchSettings_QueryPageSizeChanged, { queryPageSize: pageSize })}
                    selectedPageSizeOption={props.model?.queryPageSize}
                    onSaveSettingsCheckboxChange={checked => setIsSaveSettingsChecked(checked)}
                    isSaveSettingsChecked={isSaveSettingsChecked}
                    pageSizeOptions={[30, 50, 100]}
                />
            </div>
        </div>
    );
}

export { BillingClaimStatusSettings };
